import { useQuery } from '@tanstack/react-query';
import { analysisService } from '../services/analysis.service';
import type { Analysis, VisualizationConfigResponse } from '../services/analysis.service';

export interface UseAnalysisDataParams {
  analysis: Analysis | null;
  page?: number;
  pageSize?: number;
  sortOrder?: any[];
  filters?: any;
  dateRange?: any;
  dimensionFilters?: any[];
  customParameters?: any;
  enabled?: boolean;
}

export interface UseAnalysisDataOptions {
  enabled?: boolean;
  staleTime?: number;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
}

export function useAnalysisData(
  params: UseAnalysisDataParams,
  options?: UseAnalysisDataOptions
) {
  const {
    analysis,
    page = 1,
    pageSize = 1000,
    sortOrder,
    filters,
    dateRange,
    dimensionFilters,
    customParameters,
    enabled = true,
  } = params;

  return useQuery<VisualizationConfigResponse>({
    queryKey: [
      'analysisData',
      analysis?.id,
      analysis?.visualization_type,
      analysis?.measure_ids,
      analysis?.dimension_ids,
      analysis?.dimension_filters ? JSON.stringify(analysis.dimension_filters) : null,
      analysis?.metric_filters ? JSON.stringify(analysis.metric_filters) : null,
      analysis?.custom_parameters ? JSON.stringify(analysis.custom_parameters) : null,
      analysis?.limit,
      sortOrder ? JSON.stringify(sortOrder) : null,
      page,
      pageSize,
      filters ? JSON.stringify(filters) : null,
      dateRange ? JSON.stringify(dateRange) : null,
      dimensionFilters ? JSON.stringify(dimensionFilters) : null,
      customParameters ? JSON.stringify(customParameters) : null,
    ],
    queryFn: () => {
      if (!analysis) {
        return Promise.reject(new Error('Analysis is required'));
      }
      return analysisService.getVisualizationConfig(analysis, {
        page,
        pageSize,
        sortOrder,
        filters,
        dateRange,
        dimensionFilters,
        customParameters,
      });
    },
    enabled: !!analysis && enabled && (options?.enabled !== false),
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    retry: options?.retry ?? ((failureCount, error: any) => {
      if (error?.code === 404) {
        return false;
      }
      return failureCount < 3;
    }),
  });
}

